import { type ConfigIngestModes, type Permission } from './recordingTypes';

export type OrganizationType = 'academic' | 'corporate';
export type HostType = 'instructor' | 'teacher' | 'professor' | 'host' | 'faculty' | 'producer' | 'facilitator';
export type AssistantType =
    | 'assistant'
    | 'faculty'
    | 'co_host'
    | 'adj_faculty'
    | 'adj_facilitator'
    | 'planner'
    | 'producer';
export type StudentType = 'student' | 'participant' | 'learner';
export type RosterType = 'roster' | 'roll' | 'participants';
export type GradebookType = 'gradebook' | 'reports' | 'results' | 'outcomes' | 'feedback' | 'completions' | 'credits';
export type ProctoringType = 'proctoring_view' | 'collaboration_view';
export type DocumentType =
    | 'syllabus'
    | 'rubric'
    | 'outline'
    | 'agenda'
    | 'schedule'
    | 'overview'
    | 'curriculum'
    | 'program';
export type ZoomAppId = 'user' | 'collab' | 'teams';

export interface TerminologySettings {
    hostTitle: HostType;
    assistantsTitle: AssistantType;
    studentsTitle: StudentType;
    rosterTitle: RosterType;
    gradebookTitle: GradebookType;
    documentTitle: DocumentType;
    proctoringTitle: ProctoringType;
}

export enum UnauthUserAccess {
    Allow = 'allow',
    Deny = 'deny',
    SignIn = 'signin'
}

export enum ClassAccess {
    All = 'all',
    App = 'app',
    Lti = 'lti',
    None = 'none'
}

export enum WebExpOptions {
    Classic = 'classic',
    NextGen = 'nextgen'
}

export enum ClassJoinPermission {
    RosterOnly = 'roster only',
    AuthenticatedUsers = 'authenticated users',
    All = 'all'
}

export enum ScreenShareResolutions {
    Low = 'Low',
    Medium = 'Medium',
    High = 'High'
}

// All options here should be flat. No nested objects
export type OverridableClassOptions = {
    webExperience: WebExpOptions;
    unauthUserAccess: UnauthUserAccess;
    unauthUserText: string | null;
    allowVanillaZoom: boolean;
    classJoinPermission: ClassJoinPermission;
    rosterSelfEnrollment: boolean;
    attendanceGradeTardyScore: number; // the percentage of credit given to students who are tardy, default is 80%
    hasAiTa: boolean;
    hasFileAnnotations: boolean;
    hasPresentations: boolean;
    allowNextGenMobile: boolean;
    enablePrivateDiscussion: boolean;
    orgScreenshareRes?: ScreenShareResolutions;
    recordingInitialPermission?: Permission;
    recordingsRestrictChatParticipants: boolean;
    hasTelephony: boolean;
    autoJoinWhenSessionActive: boolean;
    allowInstructorAutoJoinPriorMinutes: number;
    allowStudentJoinPriorMinutes: number;
};

export enum ClassLevelOverrideOptions {
    NoOne = 'no one',
    AdminsOnly = 'admins only',
    AdminsAndInstructors = 'admins and instructors'
}

export type ClassOptionsOverrides = {
    [P in keyof OverridableClassOptions]?: ClassLevelOverrideOptions;
};

export type ClassLevelOptions = Partial<OverridableClassOptions>;

export interface SchoolLevelOptions extends OverridableClassOptions {
    forceWebAsChromebook?: boolean;
    azureTenantIds: string[];
    zoomAccountNumbers: number[];
    userAccountCreation?: UserAccountCreationOptions; // Default depends on if Teams
    userAccountCreationDomains: string[];
    salesTrainingMode: boolean;
    allowTabLocking: boolean;
    autoProvisionNewUsersZoom: boolean;
    autoProvisionNewUsersMicrosoft: boolean;
    disableTeamsRecording: boolean;
    subClasses: boolean;
    updateMicrosoftLicenseDays: number;
    orgScreenshareRes: ScreenShareResolutions;
    hasNextGenPolls: boolean;
    hasNextGenFiles: boolean;
    hasNextGenGoogleDrive: boolean;
    hasNextGenOneDrive: boolean;
    hasTabScreenshare: boolean;
    hasTabScreenshareShareAudio: boolean;
    hasTabScreenshareAnnotation: boolean;
    enableNextGenFilesGoogleServiceAccount: boolean;
    allowVirtualBackgroundInModeratePerfMode: boolean;
    hasAllBreakoutTranscripts: boolean;
    hasEndUserSurvey: InstructorTARestrictedAccess;
    hasEndUserPerformanceTest: boolean;
    allowCustomVirtualBackgroundUpload: boolean;
    disableCameraMicrophoneWhenRecording: boolean;
    hasNextGenSidebar: boolean;
    enableACSOptimalVideoCount: boolean;
    recordingsRestrictChatParticipants: boolean;
    recordingsDisablePublicOption: boolean;
    recordingsDisableDownloads: boolean;
    recordingsExcludeStudentsActiveSpeaker: boolean;
    enableLearnerEngagement: boolean;
    participationSort: ParticipationSortType;
    hasInMeetingTechCheck: boolean;
    hasNextGenChat: boolean;
    hasWhiteboardBORType: boolean;
    permittedEmbedDomains: string[];
    excludedEmbedDomains: string[];
    hasSSOFeature: boolean;
    hasClassAuth: boolean;
    hasMsftAuth: boolean;
    hasScheduleLinkOnClassLanding: boolean;
    useIdentifierInRoster: boolean;
    allowLmsGroupBreakouts: boolean;
    disableClassBackgrounds: boolean;
    hasNextGenChatActivityFeed: boolean;
    hasNextGenLearningToolSidebar: boolean;
    hasSyncSeatingCharts: boolean;
    allowInstructorsTranscriptDownload: boolean;
    hasBreakoutRoomCrossCommunication: boolean;
    classInvitationEmailAddressOverride: string;
    showAdvancedFollowMeOptions: boolean;
}

export enum UserAccountCreationOptions {
    AdminOnly = 'admin',
    Domains = 'domains',
    AllUsers = 'all'
}

export enum InstructorTARestrictedAccess {
    Everyone = 'everyone',
    InstructorsTAsOnly = 'instructor_ta',
    NoOne = 'no_one'
}

export enum ParticipationSortType {
    Engagement = 'engagement',
    TalkTime = 'talkTime'
}

export interface FeatureSettings {
    classDocument: boolean;
    classGradebook: boolean;
    classAssessments: boolean;
    lms: boolean;
    surveys: boolean;
    polling: boolean;
    proctoring: boolean;
    enableLogAutoUpload: boolean;
    hasPanicMode: boolean;
    hasShareFiles: boolean;
    whiteboard: boolean;
    assignments: boolean;
    chat: boolean;
    loseFocusTracking: boolean;
    webApp: boolean;
    forceWebAppChromebooks: boolean;
    allowZoom: boolean;
    allowApp: boolean;
    autoTranscriptions: boolean;
    classNotes: boolean;
    sendInviteEmails: boolean;
    showMeetingInfo: boolean;
    promptTeachersForFeedback: boolean;
    promptStudentsForFeedback: boolean;
    notifyOutdatedClassVersion: boolean;
    enforceClassMinimumVersion: boolean;
    recordingLegacyZoomList: boolean;
    recordingInitialPermission: Permission;
    recordingIngestMode: ConfigIngestModes;
    recordingParticipantsCanView: boolean;
    recordingRestrictGallery: boolean;
    recordingTabAccess: ClassAccess;
    attendanceTabAccess: ClassAccess;
    restrictSessionsRecordingsByGroups: boolean;
    google: boolean;
    msOffice: boolean;
    aiTa: boolean;
    classAccess: ClassAccess;
    allowInstructorSwap: boolean;
    enableBreakoutRoomCloudRecording: boolean;
    preventLeaveSessionRedirect: boolean;
    enableBreakoutRoomSecurity: boolean;
    whiteboardScreenshotFeature: boolean;
    instructorChatDownload: boolean;
    isBbData: boolean;
    dataPortalAdmin: boolean;
    dataPortalBusinessAnalyst: boolean;
    dataPortalClassBusinessAnalyst: boolean;
    dataPortalCustomerSupport: boolean;
    dataPortalInstructor: boolean;
    dataPortalStudent: boolean;
    allowAiForSupport: boolean;
    allowAiForInsights: boolean;
    options: SchoolLevelOptions;
    overrides: ClassOptionsOverrides;
}

export interface AuthSettings {
    vanityUrl: string;
    domains: string[];
    joinMeetingButton: boolean;
    allowPMI: boolean;
    forcedAuthentication: boolean;
    autoVerify: 'never' | 'optional' | 'always';
    unauthUserAccess: UnauthUserAccess;
    unauthUserText: string | null;
}

export interface RecordingSettings {
    autoSave: boolean;
    cloudSave: boolean;
    localSave: boolean;
    participantsCanDownload: boolean;
}

export interface ClassForTeamsSettings {
    azureTenantIds: string[];
    updateMicrosoftLicenseDays: number;
    hasAzurePhoneNumbers: boolean;
}

export interface OrgSettings {
    organizationType: OrganizationType;
    terminologySettings: TerminologySettings;
    featureSettings: FeatureSettings;
    recordingSettings: RecordingSettings;
    authSettings: AuthSettings;
    classForTeamsSettings: ClassForTeamsSettings;
    zoomAppId: ZoomAppId;
}

export interface DefaultSettings {
    academic: OrgSettings;
    corporate: OrgSettings;
}
